// The offer board (E, at the board in the market row): standing lots,
// each a swap — somebody's named stock for units of a class they want.
//
// Deliberately no search, no sort, no filter, no derived rate, no
// best-price highlight (trades.md §19): perfect price discovery deletes
// the merchant profession. You read the board top to bottom, the way you
// read a board.

import { escapeHtml } from "./escape";

interface BoardLot {
  offerId: string;
  posterName: string;
  give: { spawnName: string; className: string; units: number };
  want: { classId: string; className: string; units: number };
  age: string;
  mine: boolean;
}

interface MarketResponse {
  lots: BoardLot[];
  canPost: boolean;
  shortfall: string[];
  classes: Array<{ id: string; publicName: string }>;
}

interface HoldStack {
  location: string;
  spawnId: string;
  name: string;
  className: string;
  units: number;
}

export class MarketPanel {
  private root: HTMLDivElement;
  private body: HTMLDivElement;
  private open = false;
  private board: MarketResponse | undefined;
  private hold: HoldStack[] = [];

  constructor(
    parent: HTMLElement,
    private actions: {
      post: (spawnId: string, giveUnits: number, wantClassId: string, wantUnits: number) => void;
      fill: (offerId: string, spawnId: string) => void;
      withdraw: (offerId: string) => void;
    },
  ) {
    this.root = document.createElement("div");
    this.root.className = "panel-float market-panel";
    this.root.innerHTML = `
      <div class="panel-title">THE OFFER BOARD <span class="panel-hint">E to close</span></div>
      <div class="market-body"></div>
    `;
    this.root.style.display = "none";
    parent.append(this.root);
    this.body = this.root.querySelector(".market-body") as HTMLDivElement;
  }

  isOpen(): boolean {
    return this.open;
  }

  toggle(): void {
    this.open = !this.open;
    this.root.style.display = this.open ? "" : "none";
    if (this.open) void this.refresh();
  }

  close(): void {
    this.open = false;
    this.root.style.display = "none";
  }

  /** Re-read the board after the server answered a post/fill/withdraw. */
  refreshIfOpen(): void {
    if (this.open) void this.refresh();
  }

  private async refresh(): Promise<void> {
    this.body.innerHTML = `<div class="panel-empty">reading the board…</div>`;
    try {
      const [boardRes, invRes] = await Promise.all([fetch("/api/market"), fetch("/api/inventory")]);
      if (!boardRes.ok) {
        const err = (await boardRes.json().catch(() => ({}))) as { error?: string };
        this.body.innerHTML = `<div class="panel-empty">${escapeHtml(err.error ?? "the board is dark")}</div>`;
        return;
      }
      this.board = (await boardRes.json()) as MarketResponse;
      const inv = invRes.ok
        ? ((await invRes.json()) as { stacks?: HoldStack[]; inventory?: HoldStack[] })
        : {};
      const stacks = (inv.stacks ?? inv.inventory ?? []) as HoldStack[];
      this.hold = stacks.filter((s) => s.location === "hold" && s.units > 0);
      this.render();
    } catch {
      this.body.innerHTML = `<div class="panel-empty">the board is dark (try again)</div>`;
    }
  }

  private render(): void {
    const board = this.board;
    if (!board) return;

    const shortfall = board.shortfall.length
      ? `<div class="market-shortfall">${board.shortfall
          .map((line) => `<div class="market-shortfall-line">${escapeHtml(line)}</div>`)
          .join("")}</div>`
      : "";

    const lots = board.lots.length
      ? board.lots.map((lot) => this.lotRow(lot)).join("")
      : `<div class="panel-empty">Nothing stands on the board today.</div>`;

    const form = board.canPost
      ? this.postForm(board)
      : `<div class="market-note">The board takes lots from people the town has seen deal.</div>`;

    this.body.innerHTML = `${shortfall}<div class="market-lots">${lots}</div>${form}`;
    this.wire();
  }

  private lotRow(lot: BoardLot): string {
    const payOptions = this.hold.filter(
      (s) => s.className === lot.want.className && s.units >= lot.want.units,
    );
    const action = lot.mine
      ? `<button class="market-withdraw" data-offer="${escapeHtml(lot.offerId)}">take it down</button>`
      : payOptions.length
        ? `<select class="market-pay" data-offer="${escapeHtml(lot.offerId)}">
             ${payOptions
               .map(
                 (s) =>
                   `<option value="${escapeHtml(s.spawnId)}">pay with ${escapeHtml(s.name)}</option>`,
               )
               .join("")}
           </select>
           <button class="market-fill" data-offer="${escapeHtml(lot.offerId)}">fill it</button>`
        : `<span class="market-note">you are not carrying what it asks</span>`;
    return `
      <div class="market-lot">
        <div class="market-lot-line">
          <b>${lot.give.units} × ${escapeHtml(lot.give.spawnName)}</b>
          <span class="market-dim">(${escapeHtml(lot.give.className)})</span>
          — for ${lot.want.units} units of ${escapeHtml(lot.want.className)}
        </div>
        <div class="market-lot-sub">
          <span>${escapeHtml(lot.posterName)}</span>
          <span class="market-dim">${escapeHtml(lot.age)}</span>
          ${action}
        </div>
      </div>`;
  }

  private postForm(board: MarketResponse): string {
    if (this.hold.length === 0) {
      return `<div class="market-note">Your hold is empty; a lot is posted out of what you carry.</div>`;
    }
    return `
      <div class="market-post">
        <div class="market-post-title">STAND A LOT</div>
        <select class="market-give-spawn">
          ${this.hold
            .map(
              (s) =>
                `<option value="${escapeHtml(s.spawnId)}">${escapeHtml(s.name)} (${escapeHtml(
                  s.className,
                )}, ${s.units} carried)</option>`,
            )
            .join("")}
        </select>
        <input class="market-give-units" type="number" min="1" placeholder="units to stand" />
        <span class="market-dim">for</span>
        <select class="market-want-class">
          ${board.classes
            .map((c) => `<option value="${escapeHtml(c.id)}">${escapeHtml(c.publicName)}</option>`)
            .join("")}
        </select>
        <input class="market-want-units" type="number" min="1" placeholder="units wanted" />
        <button class="market-post-btn">stand it</button>
      </div>`;
  }

  private wire(): void {
    for (const btn of this.body.querySelectorAll<HTMLButtonElement>(".market-withdraw")) {
      btn.addEventListener("click", () => {
        const offerId = btn.dataset.offer ?? "";
        if (offerId) this.actions.withdraw(offerId);
      });
    }
    for (const btn of this.body.querySelectorAll<HTMLButtonElement>(".market-fill")) {
      btn.addEventListener("click", () => {
        const offerId = btn.dataset.offer ?? "";
        const pay = this.body.querySelector<HTMLSelectElement>(
          `.market-pay[data-offer="${CSS.escape(offerId)}"]`,
        );
        if (offerId && pay?.value) this.actions.fill(offerId, pay.value);
      });
    }
    const postBtn = this.body.querySelector<HTMLButtonElement>(".market-post-btn");
    postBtn?.addEventListener("click", () => {
      const spawn = this.body.querySelector<HTMLSelectElement>(".market-give-spawn")?.value ?? "";
      const giveUnits = Number(
        this.body.querySelector<HTMLInputElement>(".market-give-units")?.value,
      );
      const wantClass =
        this.body.querySelector<HTMLSelectElement>(".market-want-class")?.value ?? "";
      const wantUnits = Number(
        this.body.querySelector<HTMLInputElement>(".market-want-units")?.value,
      );
      if (spawn && wantClass && giveUnits > 0 && wantUnits > 0) {
        this.actions.post(spawn, giveUnits, wantClass, wantUnits);
      }
    });
  }
}
